import { useState, useCallback } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
} from "@simplewebauthn/browser";
import { setCredentials } from "state/authSlice";

const BASE = process.env.REACT_APP_BASE_URL;

// WebAuthn / passkey hook. Biometric sign-in is just another way to obtain the
// same JWT the password flow mints, so loginWithPasskey persists {user, token}
// and dispatches setCredentials exactly like useLogin — the rest of the app is
// none the wiser.
export const useWebAuthn = () => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth?.token);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // One-tap, usernameless sign-in. No email is sent; the browser shows its own
  // account picker and signs with the chosen discoverable passkey.
  const loginWithPasskey = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data: start } = await axios.post(
        `${BASE}usersServer/webauthn/login/options`
      );
      const assertion = await startAuthentication({
        optionsJSON: start.options,
      });
      const { data } = await axios.post(
        `${BASE}usersServer/webauthn/login/verify`,
        { challengeId: start.challengeId, response: assertion }
      );
      const { user, token: jwt } = data;
      localStorage.setItem("user", JSON.stringify({ user, token: jwt }));
      dispatch(setCredentials({ user, token: jwt }));
      return true;
    } catch (err) {
      // Dismissing the OS prompt throws NotAllowedError/AbortError — that's a
      // user choice, not a failure, so stay silent.
      if (err?.name === "NotAllowedError" || err?.name === "AbortError") {
        return false;
      }
      setError(err?.response?.data?.error || "Biometric sign-in failed.");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  // Enroll a passkey for the already-logged-in user (token from the store).
  const enrollPasskey = useCallback(
    async (deviceLabel) => {
      setIsLoading(true);
      setError(null);
      try {
        const cfg = { headers: { Authorization: `Bearer ${token}` } };
        const { data: start } = await axios.post(
          `${BASE}usersServer/me/webauthn/register/options`,
          {},
          cfg
        );
        const attestation = await startRegistration({
          optionsJSON: start.options,
        });
        await axios.post(
          `${BASE}usersServer/me/webauthn/register/verify`,
          { challengeId: start.challengeId, response: attestation, deviceLabel },
          cfg
        );
        return true;
      } catch (err) {
        if (err?.name === "NotAllowedError" || err?.name === "AbortError") {
          setError("Enrollment was cancelled.");
          return false;
        }
        if (err?.name === "InvalidStateError") {
          setError("This device already has a passkey for your account.");
          return false;
        }
        setError(
          err?.response?.data?.error || "Could not enable biometric sign-in."
        );
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  return { loginWithPasskey, enrollPasskey, error, isLoading, setError };
};

// Local capability probe (no network) used to gate the UI: true only when the
// browser speaks WebAuthn AND a platform authenticator (Face ID / Touch ID /
// Windows Hello / fingerprint) is actually present.
export const checkBiometricSupport = async () => {
  try {
    if (!browserSupportsWebAuthn()) return false;
    return await platformAuthenticatorIsAvailable();
  } catch {
    return false;
  }
};

// --- Local "is this browser/device enrolled" bookkeeping -------------------
// The server stores passkeys per user, not per physical device, so it can't
// tell us whether THIS browser is enrolled. We track it locally:
//   - a per-user flag drives the settings toggle's "Enabled on this device"
//   - a device-wide flag lets the Login screen decide one-tap vs "set up"
//     before any user is known.
// All best-effort (wrapped in try/catch for private-mode); the worst case is a
// failed one-tap that simply falls back to the password form.
const DEVICE_FLAG = "phieg.bioDeviceHasPasskey";
const userFlag = (userId) => `phieg.bioEnrolled.${userId || "anon"}`;

export const markEnrolledHere = (userId) => {
  try {
    localStorage.setItem(DEVICE_FLAG, "1");
    localStorage.setItem(userFlag(userId), "1");
  } catch (e) {
    /* private mode — flag is best-effort UX only */
  }
};

export const clearEnrolledHere = (userId) => {
  try {
    localStorage.removeItem(userFlag(userId));
    localStorage.removeItem(DEVICE_FLAG);
  } catch (e) {
    /* ignore */
  }
};

export const isEnrolledHere = (userId) => {
  try {
    return localStorage.getItem(userFlag(userId)) === "1";
  } catch (e) {
    return false;
  }
};

export const deviceHasAnyPasskey = () => {
  try {
    return localStorage.getItem(DEVICE_FLAG) === "1";
  } catch (e) {
    return false;
  }
};

// Coarse, friendly device label for the enrolled-passkey list. Best-effort
// from the UA — not security-relevant.
export const guessDeviceLabel = () => {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  if (/iphone|ipad/i.test(ua)) return "iPhone / iPad";
  if (/android/i.test(ua)) return "Android";
  if (/mac/i.test(ua)) return "Mac";
  if (/win/i.test(ua)) return "Windows";
  return "This device";
};
